import {
  DateFormat,
  PuxCol,
  PuxContent,
  PuxDialogTitle,
  PuxFooter,
  PuxGrid,
  PuxItem,
  PuxLabel,
  PuxModal,
  PuxRadio,
  PuxRadioGroup,
  PuxRow,
  PuxSheet,
  PuxText,
  PuxTypography,
  useBreakpoint,
  useTranslation,
} from "@phonero/pux-react"
import { SimCardFragment } from "../AvailableAndActiveSimCards.generated"
import { SimCardFragment as SimCardFragmentServices } from "../AvailableAndActiveSimCards.generated"
import {
  DeactivateSimCardDocument,
  DeactivateSimCardMutationVariables,
} from "./SimCard.generated"
import { ActiveSimCard } from "../ActiveSimCard"
import { ApiProductState, SimCardType2 } from "@phonero/common-graphql/types"
import css from "./DeactivateSimCardModal.module.scss"
import { useState } from "react"
import { DatePickerInput } from "@phonero/phonero-ui-lib"
import { useAppMutation, useAppQuery } from "../../util/gqlClientHooks"
import { WorkdaysDocument } from "../../Workdays/Workdays.generated"
import { calculateWorkdaysData } from "../../util/calculateWorkdaysData"
import { SingleOrderStatusChecker } from "../../Orders/SingleOrderStatusChecker"
import { InlineError } from "../../Error/InlineError"
import { InfoBlock } from "../../Other"
import { ActiveSimCardDetails } from "../ActiveSimCardDetails"
import { StepButtons } from "../../Wizards"
import cx from "classnames"

export const DeactivateSimCardModal = ({
  card,
  isOpen,
  onDidDismiss,
  disableDateSelection,
  showPrice
}: {
  card: SimCardFragment | SimCardFragmentServices | undefined
  isOpen: boolean
  onDidDismiss: () => void
  disableDateSelection?: boolean
  showPrice: boolean
}) => {
  const { t, i18n } = useTranslation()

  const [selectedDeactivationType, setSelectedDeactivationType] = useState<
    "now" | "choose" | ""
  >()
  const [selectedDeactivationDate, setSelectedDeactivationDate] =
    useState<Date>()
  const [deactivateSimCard, { data, loading, error }] = useAppMutation(
    DeactivateSimCardDocument,
    {}
  )

  if (error) {
    throw new Error(error.message)
  }

  const onCancel = () => {
    onDidDismiss()
    setSelectedDeactivationType("")
    setSelectedDeactivationDate(undefined)
  }

  const firstValidDate = new Date(Date.now())
  const { data: workDaysData, error: workDaysError } = useAppQuery(
    WorkdaysDocument,
    {
      variables: {
        // Months in JavaScript are indexes (0 = January) while backend expects the month number (1 = January)
        fromMonth: firstValidDate.getMonth() + 1,
        fromYear: firstValidDate.getFullYear(),
        numberOfMonths: 12,
      },
    }
  )

  const workdaysQueryResult = calculateWorkdaysData({
    workDaysData,
    nbOfWorkdaysToSkip: 1,
    excludeRedDays: true,
    lastPossiblePortingDate: null,
  })

  const done = data
  const onConfirm = () => {
    const deactivateDate = disableDateSelection
      ? new Date()
      : selectedDeactivationDate
    if (!card || !deactivateDate) return
    const variables: DeactivateSimCardMutationVariables = {
      input: {
        id: card.id,
        deactivationDateTime: deactivateDate,
      },
    }
    deactivateSimCard({
      variables,
      refetchQueries: ["AvailableAndActiveSimCards", "SubscriptionServices"],
    })
  }

  const onSelectedChanged = (e: CustomEvent) => {
    setSelectedDeactivationType(e.detail.value)
    if (e.detail.value === "now") setSelectedDeactivationDate(new Date())
    else setSelectedDeactivationDate(undefined)
  }

  const onDateChange = (date: Date) => {
    setSelectedDeactivationDate(date)
  }

  const getStatusText = () => {
    const name = !!card?.simCardName
      ? card.simCardName
      : t("SimCard.Type_" + card!.type)
    if (selectedDeactivationType === "now") return `${name} er nå avsluttet`
    return `${name} avsluttes ${i18n.format(
      done!.deactivateService!.deactivationDateTime,
      DateFormat.DateTime
    )}`
  }

  const xs = useBreakpoint("md", { useMax: true, useMin: false })

  if (!card) return <></>

  return (
    <PuxModal isOpen={isOpen} onDidDismiss={() => onCancel()}>
      <PuxDialogTitle>Avslutt SIM-kort</PuxDialogTitle>
      <PuxContent className={css.deactivateSimCardModalContent}>
        <InlineError error={workDaysError} code="Workdays" />
        {/* The width of the sheet is way to large when on mobile screens. Setting the width to vw.*/}
        <PuxSheet className={"maxSize"}>
          {(done || loading) && (
            <SingleOrderStatusChecker
              orderId={done?.deactivateService?.orderId}
              error={!!error}
              statusMessageOnCompletedGenerator={getStatusText}
            />
          )}
          {!done && !loading && (
            <>
              <div className={cx(css.deactivateSimCardInfo)}>
                {card.type === SimCardType2.Smartwatch && (
                  <InfoBlock background="light-yellow" color="orange-dark">
                    Vi anbefaler å resette klokken før SIM-kortet avsluttes.
                    Dette gjør det enklere å koble klokken til på nytt ved en
                    senere anledning.
                  </InfoBlock>
                )}
                <PuxItem className={cx(disableDateSelection && css.centered)}>
                  <div className={css.cardAndInfo}>
                    <ActiveSimCard
                      card={card}
                      updateSimCardLoading={false}
                      subscriptionState={ApiProductState.Active}
                      lastEditSimAliasId={""}
                      onEditSimAlias={null}
                    />
                    {/* Not showing details in ditt phonero. */}
                    {!disableDateSelection && (
                      <ActiveSimCardDetails
                        mode="dark"
                        deviceName={card.deviceInfo?.deviceName}
                        lastUsed={card.deviceInfo?.lastUsed}
                        usage={card.usage}
                        activationDate={card.activationDate}
                        price={card.price}
                        style={{ marginTop: xs ? 0 : "15px" }}
                        showPrice={showPrice}
                      />
                    )}
                  </div>
                </PuxItem>
              </div>
              {!disableDateSelection && (
                <>
                  <hr className={cx("underline", css.underline)} />
                  <div className={css.deactivateSimCardDateSelection}>
                    <PuxItem>
                      <PuxText>Når skal SIM-kortet avsluttes?</PuxText>
                    </PuxItem>
                    <PuxRadioGroup
                      value={selectedDeactivationType}
                      onPuxChange={onSelectedChanged}
                    >
                      <PuxRadio value="" style={{ display: "none" }}>
                        Hacky way to have no default selected option
                      </PuxRadio>
                      <PuxItem>
                        <PuxGrid>
                          <PuxRow>
                            <PuxCol size="1" size-sm="1">
                              <PuxRadio value="now"></PuxRadio>
                            </PuxCol>
                            <PuxCol>Avslutt umiddelbart</PuxCol>
                            <PuxCol size="1" size-sm="1">
                              <PuxRadio value="choose"></PuxRadio>
                            </PuxCol>
                            <PuxCol>Velg dato</PuxCol>
                          </PuxRow>
                        </PuxGrid>
                      </PuxItem>
                      {selectedDeactivationType === "choose" && (
                        <PuxItem>
                          <div style={{ lineHeight: "2.5rem" }}>
                            <PuxLabel>Dato*</PuxLabel>
                            <div className={"calendar"}>
                              <DatePickerInput
                                initialDate={
                                  workdaysQueryResult.defaultActivationDate
                                }
                                fromDate={
                                  workdaysQueryResult.defaultActivationDate
                                }
                                value={selectedDeactivationDate}
                                onDateChange={onDateChange}
                                placeholder="Velg dato"
                                hideOnDayClick={true}
                                format={DateFormat.DateLong}
                                formatDate={(date, format) =>
                                  i18n.format(date, format)
                                }
                              />
                            </div>
                            <PuxTypography
                              variant={"info-small"}
                              component={"div"}
                              style={{ opacity: 0.6 }}
                            >
                              Avsluttes kl. 23:59 på valgt dato
                            </PuxTypography>
                          </div>
                        </PuxItem>
                      )}
                    </PuxRadioGroup>
                  </div>
                </>
              )}
            </>
          )}
        </PuxSheet>
      </PuxContent>
      <PuxFooter>
        {(done || loading) && (
          <StepButtons onDone={() => onCancel()} doneText={"Lukk"} />
        )}
        {!done && !loading && (
          <StepButtons
            onCancel={() => onCancel()}
            onConfirm={() => onConfirm()}
            confirmDisabled={
              disableDateSelection ? false : !selectedDeactivationDate
            }
            confirmText={"Avslutt"}
          />
        )}
      </PuxFooter>
    </PuxModal>
  )
}
